import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  DescriptionList,
  Icon,
  TextArea,
  Typography,
  SectionCard,
  Card,
} from "@/deps/ui-library";
import { api } from "@/api";
import { useReplyToSupportTicket, useSupportTicket } from "@/api/queries";
import type { SupportAttachment } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EmptyState } from "@/components/EmptyState";
import { formatDateTime } from "@/lib/dates";
import { cn } from "@/lib/cn";
import { FilePicker } from "./FilePicker";
import { HelpGate } from "./HelpGate";
import { PlatformHtml } from "./PlatformHtml";
import { textToHtml, ticketStatusLabel, ticketStatusScheme } from "./supportLabels";

function Attachments({ files }: { files: SupportAttachment[] }) {
  if (files.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {files.map((file) => (
        <li key={file.id}>
          <a
            href={api.support.attachmentUrl(file.id)}
            download={file.fileName}
            className="border-border text-foreground hover:border-primary focus-visible:ring-ring inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs no-underline focus-visible:ring-2 focus-visible:outline-none"
          >
            <Icon name="Paperclip" size="xs" />
            {file.fileName}
          </a>
        </li>
      ))}
    </ul>
  );
}

/** One ticket: the conversation with Rigo's support team, the details, and a reply box. */
export function SupportTicketPage({ id }: { id: string }) {
  const ticket = useSupportTicket(id);
  const reply = useReplyToSupportTicket(id);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const detail = ticket.data;

  return (
    <HelpGate section="tickets">
      <Breadcrumbs
        items={[
          { label: "My tickets", to: "/help/tickets" },
          { label: detail?.ticket.ticketNo ?? "Ticket" },
        ]}
      />

      {ticket.isError ? (
        <EmptyState
          icon="SearchX"
          title="That ticket is not available"
          description="It may belong to another organisation, or the number may be wrong."
        />
      ) : null}

      {detail ? (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
          <Card size="none" className="overflow-hidden">
            <div className="border-border border-b px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="num text-foreground text-xs">{detail.ticket.ticketNo}</span>
                <Badge
                  variant="soft"
                  colorScheme={ticketStatusScheme(detail.ticket.status)}
                  size="sm"
                >
                  {ticketStatusLabel(detail.ticket.status)}
                </Badge>
              </div>
              <h2 className="text-foreground m-0 mt-1 text-base font-semibold">
                {detail.ticket.title}
              </h2>
              <Attachments files={detail.attachments} />
            </div>

            <ol aria-label="Conversation" className="m-0 flex list-none flex-col gap-3 p-4">
              {detail.messages.map((message) => (
                <li
                  key={message.id}
                  className={cn(
                    "border-border max-w-[85%] rounded-lg border px-3 py-2",
                    message.isFromAgent ? "bg-accent self-start" : "bg-card self-end",
                  )}
                >
                  <Typography.Text as="div" variant="small" muted>
                    {message.isFromAgent
                      ? `${message.authorName} · Rigo support`
                      : message.authorName}{" "}
                    · {formatDateTime(message.createdAt)}
                  </Typography.Text>
                  <PlatformHtml html={message.bodyHtml} kind="message" />
                  <Attachments files={message.attachments} />
                </li>
              ))}
            </ol>

            <form
              className="border-border flex flex-col gap-3 border-t p-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!text.trim()) return;
                reply.mutate(
                  { bodyHtml: textToHtml(text), files },
                  {
                    onSuccess: () => {
                      setText("");
                      setFiles([]);
                    },
                  },
                );
              }}
            >
              {reply.error ? (
                <Alert
                  config={{ variant: "subtle", colorScheme: "danger" }}
                  description={reply.error.message}
                />
              ) : null}
              <TextArea
                label="Reply"
                rows={4}
                value={text}
                placeholder="Write to Rigo's support team"
                onChange={(event) => setText(event.target.value)}
              />
              <FilePicker files={files} onChange={setFiles} />
              <div className="flex justify-end">
                <Button
                  colorScheme="primary"
                  type="submit"
                  disabled={!text.trim()}
                  loading={reply.isPending}
                >
                  Send reply
                </Button>
              </div>
            </form>
          </Card>

          <SectionCard title="Details">
            <DescriptionList
              variant="none"
              compact
              flush
              cardBordered={false}
              items={[
                { label: "Area", value: detail.ticket.category },
                { label: "Raised by", value: detail.ticket.raisedByName ?? "—" },
                {
                  label: "Handled by",
                  value: detail.ticket.assignedAgentName ?? "Not yet assigned",
                },
                { label: "Raised", value: formatDateTime(detail.ticket.createdAt) },
                { label: "Last update", value: formatDateTime(detail.ticket.lastActivityAt) },
              ]}
            />
          </SectionCard>
        </div>
      ) : null}
    </HelpGate>
  );
}
